import React, { useEffect, useMemo, useState } from 'react';
import { BookOpen, Eye, Users, Clock, Globe, Smartphone, Link2, TrendingUp, ExternalLink } from 'lucide-react';
import s from './adminStyles';
import { authHeaders } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RANGE_OPTIONS = [
  { key: 7, label: '7 dagen' },
  { key: 30, label: '30 dagen' },
  { key: 90, label: '90 dagen' },
  { key: 365, label: '1 jaar' },
];

const DEVICE_LABEL = { mobile: 'Mobiel', tablet: 'Tablet', desktop: 'Desktop' };

// Convert 2-letter ISO country codes to a flag emoji + name (minimal common set).
const COUNTRY_NAMES = {
  NL: 'Nederland', BE: 'België', DE: 'Duitsland', ES: 'Spanje', FR: 'Frankrijk',
  GB: 'Verenigd Koninkrijk', US: 'Verenigde Staten', LU: 'Luxemburg', IT: 'Italië',
  PT: 'Portugal', SE: 'Zweden', NO: 'Noorwegen', DK: 'Denemarken', PL: 'Polen',
  IE: 'Ierland', CH: 'Zwitserland', AT: 'Oostenrijk', FI: 'Finland',
};
const flagFor = (code) => {
  if (!code || code.length !== 2) return '🏳️';
  return String.fromCodePoint(...[...code.toUpperCase()].map(c => 0x1F1E6 + c.charCodeAt(0) - 65));
};

const formatSeconds = (sec) => {
  if (!sec) return '0s';
  const m = Math.floor(sec / 60);
  const rest = Math.round(sec % 60);
  return m ? `${m}m ${rest}s` : `${rest}s`;
};

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const DailyChart = ({ data }) => {
  if (!data || data.length === 0) return <p style={styles.muted}>Nog geen data.</p>;
  const max = Math.max(...data.map(d => d.count), 1);
  const width = 800;
  const height = 120;
  const pad = 4;
  const stepX = (width - pad * 2) / Math.max(data.length - 1, 1);
  const points = data.map((d, i) => {
    const x = pad + i * stepX;
    const y = pad + (height - pad * 2) * (1 - d.count / max);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  const area = `${pad},${height - pad} ${points} ${(pad + (data.length - 1) * stepX).toFixed(1)},${height - pad}`;
  return (
    <div style={styles.chartWrap}>
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" style={{ width: '100%', height: '160px' }}>
        <polygon points={area} fill="#C5A05922" />
        <polyline points={points} fill="none" stroke="#C5A059" strokeWidth="2" />
        {data.map((d, i) => {
          const x = pad + i * stepX;
          const y = pad + (height - pad * 2) * (1 - d.count / max);
          return <circle key={d.date} cx={x} cy={y} r="2.2" fill="#1A1918">
            <title>{`${d.date}: ${d.count} views`}</title>
          </circle>;
        })}
      </svg>
      <div style={styles.chartLegend}>
        <span>{data[0]?.date}</span>
        <span>Piek: {max}</span>
        <span>{data[data.length - 1]?.date}</span>
      </div>
    </div>
  );
};

const BreakdownList = ({ title, icon: Icon, items, decorateLabel }) => {
  const total = items.reduce((sum, i) => sum + i.count, 0) || 1;
  return (
    <div style={styles.card} data-testid={`breakdown-${title.toLowerCase()}`}>
      <h3 style={styles.cardTitle}><Icon size={16} style={{ verticalAlign: 'middle', marginRight: '0.4rem' }} />{title}</h3>
      {items.length === 0 ? <p style={styles.muted}>Nog geen data.</p> : (
        <div style={styles.breakdownList}>
          {items.slice(0, 10).map(({ label, count }) => {
            const pct = (count / total) * 100;
            return (
              <div key={label} style={styles.breakdownRow}>
                <span style={styles.breakdownLabel}>{decorateLabel ? decorateLabel(label) : label}</span>
                <div style={styles.breakdownBarWrap}>
                  <div style={{ ...styles.breakdownBar, width: `${pct}%` }} />
                </div>
                <span style={styles.breakdownCount}>{count}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const BlogStatsPanel = () => {
  const [stats, setStats] = useState(null);
  const [range, setRange] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetch(`${API_URL}/api/admin/blog-stats?range_days=${range}`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(d => { setStats(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [range]);

  const sortedBlogs = useMemo(() => {
    if (!stats?.blogs) return [];
    return [...stats.blogs].sort((a, b) => b.rangeViews - a.rangeViews);
  }, [stats]);

  return (
    <div data-testid="blog-stats-panel">
      <div style={styles.headerRow}>
        <div>
          <h2 style={s.sectionTitle}>Blog Statistieken</h2>
          <p style={styles.subtitle}>
            Anonieme, GDPR-conforme leesstatistieken — geen cookies, geen persoonlijke data.
          </p>
        </div>
        <div style={styles.rangeGroup} data-testid="range-selector">
          {RANGE_OPTIONS.map(opt => (
            <button
              key={opt.key}
              onClick={() => setRange(opt.key)}
              style={range === opt.key ? styles.rangeBtnActive : styles.rangeBtn}
              data-testid={`range-${opt.key}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {loading && <p style={styles.muted}>Statistieken laden…</p>}
      {error && <p style={styles.error}>Fout bij laden: {error}</p>}

      {stats && !loading && (
        <>
          {/* KPI cards */}
          <div style={styles.kpiGrid}>
            <KpiCard icon={Eye} label="Totaal views" value={stats.totalViews} accent="#1A1918" testId="kpi-total-views" />
            <KpiCard icon={Users} label="Unieke sessies" value={stats.totalSessions} accent="#C5A059" testId="kpi-unique-sessions" />
            <KpiCard icon={BookOpen} label="Actieve blogs" value={sortedBlogs.length} accent="#3B82F6" testId="kpi-active-blogs" />
            <KpiCard
              icon={Clock}
              label="Gem. leestijd"
              value={formatSeconds(
                sortedBlogs.length
                  ? sortedBlogs.reduce((sum, b) => sum + b.avgReadSeconds * b.uniqueSessions, 0) /
                    Math.max(sortedBlogs.reduce((sum, b) => sum + b.uniqueSessions, 0), 1)
                  : 0
              )}
              accent="#22C55E"
              testId="kpi-avg-read"
            />
          </div>

          {/* Daily views chart */}
          <div style={styles.card} data-testid="daily-chart-card">
            <h3 style={styles.cardTitle}>
              <TrendingUp size={16} style={{ verticalAlign: 'middle', marginRight: '0.4rem' }} />
              Views per dag
            </h3>
            <DailyChart data={stats.daily || []} />
          </div>

          {/* Per-blog table */}
          <div style={styles.card} data-testid="blog-table-card">
            <h3 style={styles.cardTitle}>
              <BookOpen size={16} style={{ verticalAlign: 'middle', marginRight: '0.4rem' }} />
              Per blog
            </h3>
            {sortedBlogs.length === 0 ? (
              <p style={styles.muted}>Nog geen blog-views geregistreerd in deze periode.</p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={styles.table} data-testid="blog-stats-table">
                  <thead>
                    <tr style={styles.tableHeaderRow}>
                      <th style={styles.thLeft}>Blog</th>
                      <th style={styles.thRight}>Views</th>
                      <th style={styles.thRight}>Unieke sessies</th>
                      <th style={styles.thRight}>Gem. leestijd</th>
                      <th style={styles.thRight}>Laatste view</th>
                      <th style={styles.thRight}>Totaal (all-time)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedBlogs.map((b, i) => (
                      <tr key={b.slug} style={styles.tableRow} data-testid={`blog-row-${i}`}>
                        <td style={styles.tdBlog}>
                          <a href={`/blog/${b.slug}`} target="_blank" rel="noopener noreferrer" style={styles.blogLink}>
                            {b.title || b.slug}
                            <ExternalLink size={11} style={{ marginLeft: '0.35rem', verticalAlign: 'middle', opacity: 0.6 }} />
                          </a>
                          {b.category && <div style={styles.blogCategory}>{b.category}</div>}
                        </td>
                        <td style={styles.tdRight}>{b.rangeViews}</td>
                        <td style={styles.tdRight}>{b.uniqueSessions}</td>
                        <td style={styles.tdRight}>{formatSeconds(b.avgReadSeconds)}</td>
                        <td style={styles.tdRightMuted}>{formatDate(b.lastView)}</td>
                        <td style={styles.tdRightGold}>{b.totalViews}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Breakdown grid */}
          <div style={styles.breakdownGrid}>
            <BreakdownList
              title="Landen"
              icon={Globe}
              items={stats.countries || []}
              decorateLabel={(code) => `${flagFor(code)} ${COUNTRY_NAMES[code] || code}`}
            />
            <BreakdownList
              title="Apparaten"
              icon={Smartphone}
              items={stats.devices || []}
              decorateLabel={(v) => DEVICE_LABEL[v] || v}
            />
            <BreakdownList
              title="Herkomst"
              icon={Link2}
              items={stats.referrers || []}
            />
          </div>
        </>
      )}
    </div>
  );
};

const KpiCard = ({ icon: Icon, label, value, accent, testId }) => (
  <div style={styles.kpiCard} data-testid={testId}>
    <div style={{ ...styles.kpiIconWrap, backgroundColor: `${accent}15` }}>
      <Icon size={20} color={accent} />
    </div>
    <div>
      <div style={styles.kpiValue}>{value}</div>
      <div style={styles.kpiLabel}>{label}</div>
    </div>
  </div>
);

const styles = {
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: '1rem',
    marginBottom: '1.5rem',
  },
  subtitle: { color: '#8C8B88', fontSize: '0.88rem', margin: '0.5rem 0 0' },
  muted: { color: '#8C8B88', fontSize: '0.9rem', fontStyle: 'italic' },
  error: { color: '#DC2626', fontSize: '0.9rem' },
  rangeGroup: { display: 'flex', gap: '0.4rem', flexWrap: 'wrap' },
  rangeBtn: {
    padding: '0.5rem 0.9rem',
    fontSize: '0.82rem',
    backgroundColor: 'white',
    border: '1px solid #EAE5DD',
    color: '#585755',
    cursor: 'pointer',
  },
  rangeBtnActive: {
    padding: '0.5rem 0.9rem',
    fontSize: '0.82rem',
    backgroundColor: '#1A1918',
    border: '1px solid #1A1918',
    color: '#C5A059',
    cursor: 'pointer',
    fontWeight: 600,
  },
  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '0.85rem',
    marginBottom: '1.25rem',
  },
  kpiCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.85rem',
    padding: '1.25rem',
    backgroundColor: 'white',
    border: '1px solid #EAE5DD',
  },
  kpiIconWrap: {
    width: '40px', height: '40px',
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  kpiValue: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '1.85rem',
    color: '#1A1918',
    lineHeight: 1.1,
  },
  kpiLabel: { color: '#8C8B88', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: '0.25rem' },
  card: {
    backgroundColor: 'white',
    border: '1px solid #EAE5DD',
    padding: '1.5rem',
    marginBottom: '1.25rem',
  },
  cardTitle: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '1.1rem',
    color: '#1A1918',
    margin: '0 0 1rem',
  },
  chartWrap: { width: '100%' },
  chartLegend: {
    display: 'flex',
    justifyContent: 'space-between',
    color: '#8C8B88',
    fontSize: '0.72rem',
    marginTop: '0.4rem',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.88rem',
  },
  tableHeaderRow: {
    borderBottom: '1px solid #EAE5DD',
  },
  thLeft: {
    textAlign: 'left', padding: '0.6rem 0.5rem',
    color: '#8C8B88', fontSize: '0.75rem', textTransform: 'uppercase',
    letterSpacing: '0.05em', fontWeight: 600,
  },
  thRight: {
    textAlign: 'right', padding: '0.6rem 0.5rem',
    color: '#8C8B88', fontSize: '0.75rem', textTransform: 'uppercase',
    letterSpacing: '0.05em', fontWeight: 600,
  },
  tableRow: { borderBottom: '1px solid #F5F1E8' },
  tdBlog: { padding: '0.7rem 0.5rem', maxWidth: '320px' },
  tdRight: { padding: '0.7rem 0.5rem', textAlign: 'right', color: '#1A1918', fontWeight: 600 },
  tdRightMuted: { padding: '0.7rem 0.5rem', textAlign: 'right', color: '#8C8B88', fontSize: '0.82rem' },
  tdRightGold: { padding: '0.7rem 0.5rem', textAlign: 'right', color: '#C5A059', fontWeight: 600 },
  blogLink: { color: '#1A1918', textDecoration: 'none', fontWeight: 500 },
  blogCategory: { color: '#8C8B88', fontSize: '0.72rem', marginTop: '0.2rem', textTransform: 'uppercase', letterSpacing: '0.04em' },
  breakdownGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    gap: '1rem',
  },
  breakdownList: { display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  breakdownRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(100px, 1fr) minmax(60px, 2fr) auto',
    alignItems: 'center',
    gap: '0.75rem',
  },
  breakdownLabel: { color: '#1A1918', fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  breakdownBarWrap: { height: '6px', backgroundColor: '#F5F1E8', overflow: 'hidden' },
  breakdownBar: { height: '100%', backgroundColor: '#C5A059' },
  breakdownCount: { color: '#1A1918', fontSize: '0.85rem', fontWeight: 600, textAlign: 'right', minWidth: '32px' },
};

export default BlogStatsPanel;
